import React, { useEffect, useState, useCallback, useRef } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import {
  configureDigitalMenu,
  getMyDigitalMenu,
  checkMenuSlug,
  uploadMenuBanner,
  getMenuQRCode,
  getMenuStats,
  getMenuLicenseStatus,
  activateMenuLicense,
  getBranches,
} from '../../services/api';
import { API_URL } from '../../config';
import {
  QrCode, Settings, Eye, BarChart3, Save, Upload, Check, X, Loader2,
  Link2, Globe, ImageIcon, Facebook, Instagram, Phone, Mail, MapPin,
  Plus, Trash2, Download, ExternalLink, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const SOCIAL_PLATFORMS = [
  { value: 'facebook', label: 'Facebook', icon: Facebook },
  { value: 'instagram', label: 'Instagram', icon: Instagram },
  { value: 'tiktok', label: 'TikTok', icon: Globe },
  { value: 'whatsapp', label: 'WhatsApp', icon: Phone },
  { value: 'twitter', label: 'X / Twitter', icon: Globe },
];

const TABS = [
  { key: 'config', label: 'Configuración', icon: Settings },
  { key: 'preview', label: 'Vista Previa', icon: Eye },
  { key: 'qr', label: 'Código QR', icon: QrCode },
  { key: 'stats', label: 'Estadísticas', icon: BarChart3 },
];

const MenuDigital = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('config');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [menu, setMenu] = useState(null);

  // Config form
  const [slug, setSlug] = useState('');
  const [slugStatus, setSlugStatus] = useState(null); // null | 'checking' | 'available' | 'taken'
  const [slugSuggestions, setSuggestions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [bannerImage, setBannerImage] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Footer
  const [footerAddress, setFooterAddress] = useState('');
  const [footerPhone, setFooterPhone] = useState('');
  const [footerEmail, setFooterEmail] = useState('');
  const [socialLinks, setSocialLinks] = useState([]);

  // QR
  const [qrBlob, setQrBlob] = useState(null);

  // Stats
  const [stats, setStats] = useState(null);

  // License
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [licenseLoading, setLicenseLoading] = useState(true);

  const slugTimer = useRef(null);

  useEffect(() => {
    checkLicense();
    loadBranches();
  }, []);

  const loadBranches = async () => {
    try {
      const res = await getBranches(user?.business_id);
      setBranches((res.data || []).filter((b) => b.is_active !== false));
    } catch {
      // Sin sucursales el selector queda vacío; el backend usa la primera activa
    }
  };

  const checkLicense = async () => {
    try {
      setLicenseLoading(true);
      const res = await getMenuLicenseStatus();
      setLicenseStatus(res.data);
      if (res.data.has_valid_license) {
        loadMenu();
      } else {
        setLoading(false);
      }
    } catch {
      // If license check fails, still try to load menu
      loadMenu();
    } finally {
      setLicenseLoading(false);
    }
  };

  const loadMenu = async () => {
    try {
      setLoading(true);
      const res = await getMyDigitalMenu();
      const data = res.data;
      if (data) {
        setMenu(data);
        setSlug(data.slug || '');
        setBranchId(data.branch_id || '');
        setIsPublished(data.is_published || false);
        setBannerPreview(data.banner_image ? `${API_URL}${data.banner_image}` : null);
        const footer = data.footer || {};
        setFooterAddress(footer.address || '');
        setFooterPhone(footer.phone || '');
        setFooterEmail(footer.email || '');
        setSocialLinks(footer.social_links || []);
        setSlugStatus('available');
      }
    } catch (err) {
      // No menu yet, that's fine
    } finally {
      setLoading(false);
    }
  };

  // Slug validation with debounce
  const handleSlugChange = useCallback((value) => {
    const cleaned = value.toLowerCase().replace(/[^a-z0-9\-]/g, '').replace(/\s+/g, '-');
    setSlug(cleaned);
    setSlugStatus('checking');
    setSuggestions([]);

    if (slugTimer.current) clearTimeout(slugTimer.current);

    if (cleaned.length < 3) {
      setSlugStatus(null);
      return;
    }

    slugTimer.current = setTimeout(async () => {
      try {
        const res = await checkMenuSlug(cleaned);
        const data = res.data;
        if (data.available) {
          setSlugStatus('available');
        } else {
          setSlugStatus('taken');
          setSuggestions(data.suggestions || []);
        }
      } catch {
        setSlugStatus(null);
      }
    }, 500);
  }, []);

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Preview
    const reader = new FileReader();
    reader.onload = (ev) => setBannerPreview(ev.target.result);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await uploadMenuBanner(formData);
      setBannerImage(res.data.image_url);
      toast.success('Banner subido');
    } catch (err) {
      toast.error('Error al subir el banner');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!slug || slug.length < 3) {
      toast.error('El slug debe tener al menos 3 caracteres');
      return;
    }
    if (slugStatus === 'taken') {
      toast.error('Este slug ya está en uso');
      return;
    }

    setSaving(true);
    try {
      const data = {
        slug,
        branch_id: branchId || null,
        banner_image: bannerImage || menu?.banner_image || null,
        footer: {
          address: footerAddress,
          phone: footerPhone,
          email: footerEmail,
          social_links: socialLinks,
        },
        is_published: isPublished,
      };
      const res = await configureDigitalMenu(data);
      setMenu(res.data);
      toast.success('Menú actualizado');
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Se requiere una licencia activa para configurar el menú digital');
      } else {
        toast.error(err.response?.data?.detail || 'Error al guardar');
      }
    } finally {
      setSaving(false);
    }
  };

  // Social links
  const addSocialLink = () => {
    setSocialLinks([...socialLinks, { platform: 'facebook', url: '' }]);
  };

  const removeSocialLink = (idx) => {
    setSocialLinks(socialLinks.filter((_, i) => i !== idx));
  };

  const updateSocialLink = (idx, field, value) => {
    const updated = [...socialLinks];
    updated[idx] = { ...updated[idx], [field]: value };
    setSocialLinks(updated);
  };

  // QR
  const loadQR = async () => {
    try {
      const res = await getMenuQRCode();
      setQrBlob(res.data);
    } catch {
      toast.error('Configura tu menú primero para generar el QR');
    }
  };

  const downloadQR = () => {
    if (!qrBlob) return;
    const url = URL.createObjectURL(qrBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `menu-qr-${slug}.png`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Stats
  const loadStats = async () => {
    try {
      const res = await getMenuStats();
      setStats(res.data);
    } catch {
      setStats(null);
    }
  };

  useEffect(() => {
    if (tab === 'qr' && !qrBlob && menu) loadQR();
    if (tab === 'stats') loadStats();
  }, [tab]);

  if (loading || licenseLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      </DashboardLayout>
    );
  }

  // License gate: show plans if no valid license
  if (licenseStatus && !licenseStatus.has_valid_license) {
    const packages = licenseStatus.available_packages || [];
    const pendingLicense = licenseStatus.pending_license;

    const handleActivateLicense = async () => {
      try {
        setSaving(true);
        await activateMenuLicense();
        toast.success('Licencia activada exitosamente');
        checkLicense();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Error al activar la licencia');
      } finally {
        setSaving(false);
      }
    };

    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto py-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
              <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Menú Digital</h1>
            <p className="text-slate-500 mt-2 max-w-md mx-auto">
              {pendingLicense
                ? 'Tienes una licencia pendiente de activación'
                : 'Para configurar y publicar tu menú digital necesitas una licencia activa'}
            </p>
            {licenseStatus.license?.expired && (
              <p className="text-amber-600 text-sm mt-2 font-medium">
                Tu licencia ha expirado. Renueva para continuar usando el menú digital.
              </p>
            )}
          </div>

          {pendingLicense ? (
            <div className="max-w-md mx-auto">
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-teal-50 to-emerald-50">
                  <h3 className="font-bold text-slate-800 text-lg">Licencia Pendiente</h3>
                  <p className="text-sm text-slate-500 mt-1">Tu licencia está lista para ser activada</p>
                </div>
                <div className="p-6 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Serial</span>
                    <span className="font-mono text-slate-700">{pendingLicense.serial_number}</span>
                  </div>
                  {pendingLicense.valid_until && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Válida hasta</span>
                      <span className="text-slate-700">
                        {new Date(pendingLicense.valid_until).toLocaleDateString('es-MX')}
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-6 pt-0">
                  <button
                    onClick={handleActivateLicense}
                    disabled={saving}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {saving ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Check className="w-5 h-5" />
                    )}
                    {saving ? 'Activando...' : 'Activar Licencia'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {packages.length > 0 && (
                <>
                  <h2 className="text-lg font-semibold text-slate-800 mb-4 text-center">Planes Disponibles</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                    {packages.map((pkg) => (
                      <div key={pkg.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow">
                        <div className="p-5 border-b border-slate-100 bg-gradient-to-r from-teal-50 to-emerald-50">
                          <h3 className="font-bold text-slate-800 text-lg">{pkg.name}</h3>
                          {pkg.description && <p className="text-sm text-slate-500 mt-1">{pkg.description}</p>}
                          <p className="text-2xl font-bold text-teal-600 mt-3">
                            ${pkg.base_price_mxn}<span className="text-sm font-normal text-slate-400">/mes</span>
                          </p>
                        </div>
                        <div className="p-5">
                          {pkg.features?.length > 0 && (
                            <ul className="space-y-2 mb-4">
                              {pkg.features.map((f, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                                  <Check className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                                  {f}
                                </li>
                              ))}
                            </ul>
                          )}
                          {pkg.included_resources?.length > 0 && (
                            <div className="pt-3 border-t border-slate-100">
                              <p className="text-xs font-medium text-slate-400 uppercase mb-2">Incluye</p>
                              {pkg.included_resources.map((r, i) => (
                                <p key={i} className="text-sm text-slate-600">
                                  {r.quantity} {r.label || r.code}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="text-center">
                <a
                  href="/dashboard/plan"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700"
                >
                  Ver Plan y Complementos
                </a>
              </div>
            </>
          )}
        </div>
      </DashboardLayout>
    );
  }

  const menuUrl = slug ? `https://todoenlocal.com/menu/@${slug}` : null;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Menú Digital</h1>
            <p className="text-slate-500 mt-1">Configura tu menú público para clientes</p>
          </div>
          {menuUrl && menu && (
            <a
              href={`/menu/@${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700"
            >
              <ExternalLink className="w-4 h-4" />
              Ver menú
            </a>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-6">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors flex-1 justify-center ${
                  tab === t.key
                    ? 'bg-white text-teal-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            );
          })}
        </div>

        {/* Config Tab */}
        {tab === 'config' && (
          <div className="space-y-6">
            {/* Slug */}
            <div className="bg-white rounded-xl border p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Link2 className="w-5 h-5 text-teal-600" />
                Dirección del Menú
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 text-sm whitespace-nowrap">todoenlocal.com/menu/@</span>
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={slug}
                    onChange={(e) => handleSlugChange(e.target.value)}
                    placeholder="mi-restaurante"
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
                      slugStatus === 'available'
                        ? 'border-green-300 focus:ring-green-200'
                        : slugStatus === 'taken'
                        ? 'border-red-300 focus:ring-red-200'
                        : 'border-slate-300 focus:ring-teal-200'
                    }`}
                  />
                  {slugStatus === 'checking' && (
                    <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-slate-400" />
                  )}
                  {slugStatus === 'available' && (
                    <Check className="absolute right-3 top-2.5 w-4 h-4 text-green-500" />
                  )}
                  {slugStatus === 'taken' && (
                    <X className="absolute right-3 top-2.5 w-4 h-4 text-red-500" />
                  )}
                </div>
              </div>
              {slugStatus === 'taken' && slugSuggestions.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm text-red-600 mb-1">Este nombre no está disponible. Sugerencias:</p>
                  <div className="flex gap-2 flex-wrap">
                    {slugSuggestions.map((s) => (
                      <button
                        key={s}
                        onClick={() => { setSlug(s); setSlugStatus('available'); setSuggestions([]); }}
                        className="px-3 py-1 text-xs bg-teal-50 text-teal-700 rounded-full hover:bg-teal-100"
                      >
                        @{s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sucursal que recibe los pedidos */}
            <div className="bg-white rounded-xl border p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-1 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-teal-600" />
                Sucursal que atiende los pedidos
              </h2>
              <p className="text-sm text-slate-500 mb-4">
                Los pedidos de este menú llegan a la caja de esta sucursal, y el inventario
                y el costo de envío se calculan con sus datos.
              </p>
              <select
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-200"
              >
                {!branchId && <option value="">Selecciona una sucursal</option>}
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              {branches.length > 1 && (
                <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  Tu negocio tiene {branches.length} sucursales. Este menú envía todos sus
                  pedidos a la sucursal seleccionada; las demás no los reciben.
                </p>
              )}
            </div>

            {/* Banner */}
            <div className="bg-white rounded-xl border p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-teal-600" />
                Banner del Menú
              </h2>
              {bannerPreview && (
                <div className="mb-4 rounded-lg overflow-hidden border">
                  <img src={bannerPreview} alt="Banner" className="w-full h-40 object-cover" />
                </div>
              )}
              <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-teal-400 transition-colors">
                {uploading ? (
                  <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
                ) : (
                  <Upload className="w-5 h-5 text-slate-400" />
                )}
                <span className="text-sm text-slate-500">
                  {uploading ? 'Subiendo...' : 'Subir imagen de banner (recomendado: 1200x400)'}
                </span>
                <input type="file" accept="image/*" onChange={handleBannerUpload} className="hidden" />
              </label>
            </div>

            {/* Footer Info */}
            <div className="bg-white rounded-xl border p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-teal-600" />
                Información de Contacto
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-600 mb-1 flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> Dirección
                  </label>
                  <input
                    type="text"
                    value={footerAddress}
                    onChange={(e) => setFooterAddress(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="Calle, número, colonia..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 mb-1 flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" /> Teléfono
                  </label>
                  <input
                    type="text"
                    value={footerPhone}
                    onChange={(e) => setFooterPhone(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-slate-600 mb-1 flex items-center gap-1">
                    <Mail className="w-3.5 h-3.5" /> Email
                  </label>
                  <input
                    type="email"
                    value={footerEmail}
                    onChange={(e) => setFooterEmail(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="contacto@minegocio.com"
                  />
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="bg-white rounded-xl border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-800">Redes Sociales</h2>
                <button
                  onClick={addSocialLink}
                  className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700"
                >
                  <Plus className="w-4 h-4" /> Agregar
                </button>
              </div>
              {socialLinks.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">No hay redes sociales configuradas</p>
              )}
              <div className="space-y-3">
                {socialLinks.map((link, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <select
                      value={link.platform}
                      onChange={(e) => updateSocialLink(idx, 'platform', e.target.value)}
                      className="px-3 py-2 border rounded-lg text-sm w-36"
                    >
                      {SOCIAL_PLATFORMS.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={link.url}
                      onChange={(e) => updateSocialLink(idx, 'url', e.target.value)}
                      placeholder="https://..."
                      className="flex-1 px-3 py-2 border rounded-lg text-sm"
                    />
                    <button onClick={() => removeSocialLink(idx)} className="p-2 text-red-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Publish toggle + Save */}
            <div className="bg-white rounded-xl border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-slate-800">Publicar Menú</h3>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {isPublished ? 'Tu menú es visible para clientes' : 'Tu menú no es visible aún'}
                  </p>
                </div>
                <button
                  onClick={() => setIsPublished(!isPublished)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    isPublished ? 'bg-teal-500' : 'bg-slate-300'
                  }`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    isPublished ? 'translate-x-6' : ''
                  }`} />
                </button>
              </div>
            </div>

            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              {saving ? 'Guardando...' : 'Guardar Configuración'}
            </button>
          </div>
        )}

        {/* Preview Tab */}
        {tab === 'preview' && (
          <div className="bg-white rounded-xl border overflow-hidden">
            {menu && slug ? (
              <iframe
                src={`/menu/@${slug}`}
                className="w-full h-[700px] border-0"
                title="Vista previa del menú"
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                <Eye className="w-12 h-12 mb-3" />
                <p className="text-lg font-medium">Configura tu menú primero</p>
                <p className="text-sm mt-1">Guarda la configuración para ver la vista previa</p>
              </div>
            )}
          </div>
        )}

        {/* QR Tab */}
        {tab === 'qr' && (
          <div className="bg-white rounded-xl border p-8 text-center">
            {qrBlob ? (
              <>
                <img
                  src={URL.createObjectURL(qrBlob)}
                  alt="QR Code"
                  className="w-64 h-64 mx-auto mb-4 rounded-lg border"
                />
                <p className="text-sm text-slate-500 mb-1">Escanea para abrir tu menú</p>
                {menuUrl && (
                  <p className="text-xs text-teal-600 mb-6 font-mono">{menuUrl}</p>
                )}
                <button
                  onClick={downloadQR}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700"
                >
                  <Download className="w-5 h-5" />
                  Descargar QR (1024x1024)
                </button>
              </>
            ) : (
              <div className="py-12">
                {menu ? (
                  <>
                    <Loader2 className="w-8 h-8 animate-spin text-teal-600 mx-auto mb-3" />
                    <p className="text-slate-500">Generando código QR...</p>
                  </>
                ) : (
                  <>
                    <QrCode className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Configura tu menú primero para generar el QR</p>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Stats Tab */}
        {tab === 'stats' && (
          <div className="space-y-6">
            {stats ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl border p-6 text-center">
                    <p className="text-3xl font-bold text-teal-600">{stats.monthly_sales}</p>
                    <p className="text-sm text-slate-500 mt-1">Pedidos este mes</p>
                  </div>
                  <div className="bg-white rounded-xl border p-6 text-center">
                    <p className="text-3xl font-bold text-slate-800">
                      {stats.quota ? stats.quota.included : '—'}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">Incluidos en plan</p>
                  </div>
                  <div className="bg-white rounded-xl border p-6 text-center">
                    <p className={`text-3xl font-bold ${stats.extra_sales > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                      {stats.extra_sales}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">Pedidos extra</p>
                  </div>
                </div>

                {stats.extra_sales > 0 && stats.extra_sales_cost > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">
                        Has superado tu cuota mensual por {stats.extra_sales} pedido(s)
                      </p>
                      <p className="text-sm text-amber-600 mt-0.5">
                        Costo extra estimado: ${stats.extra_sales_cost.toFixed(2)} MXN
                        {stats.quota && ` (${stats.quota.extra_sale_price} por pedido extra)`}
                      </p>
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-xl border p-6">
                  <h3 className="font-medium text-slate-800 mb-2">Periodo: {stats.month}</h3>
                  <div className="w-full bg-slate-100 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full ${stats.extra_sales > 0 ? 'bg-amber-500' : 'bg-teal-500'}`}
                      style={{
                        width: stats.quota
                          ? `${Math.min(100, (stats.monthly_sales / stats.quota.included) * 100)}%`
                          : '0%',
                      }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5">
                    {stats.monthly_sales} / {stats.quota ? stats.quota.included : '?'} pedidos
                  </p>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl border p-12 text-center text-slate-400">
                <BarChart3 className="w-12 h-12 mx-auto mb-3" />
                <p>No hay estadísticas disponibles</p>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MenuDigital;
